import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Avatar,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
} from "antd";
import { PlusOutlined, RiseOutlined, SearchOutlined } from "@ant-design/icons";
import { postReq, getReq } from "../api";
import dayjs from "dayjs";

const { Text } = Typography;
const url = process.env.REACT_APP_API_URL;

// Native month input styled to match Ant Design
const MonthInput = ({ value, onChange, min, error, placeholder }) => (
  <input
    type="month"
    value={value}
    min={min}
    onChange={onChange}
    placeholder={placeholder}
    style={{
      width: "100%",
      padding: "7px 11px",
      borderRadius: 6,
      border: `1px solid ${error ? "#ff4d4f" : "#d9d9d9"}`,
      fontSize: 14,
      outline: "none",
      color: "inherit",
    }}
  />
);

const Salary = () => {
  // ── Add Salary form ──────────────────────────────────────────────────────
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      userId: "",
      monthlyCTC: "",
      effectiveFrom: "",
      isAttendanceRequired: "true",
    },
  });

  // ── Appraisal form ───────────────────────────────────────────────────────
  const {
    control: appraisalControl,
    handleSubmit: handleAppraisalSubmit,
    reset: resetAppraisal,
    formState: { errors: appraisalErrors },
  } = useForm({
    defaultValues: {
      newMonthlyCTC: "",
      effectiveFrom: "",
      isAttendanceRequired: "true",
    },
  });

  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 1,
    pageSize: 10,
    search: "",
  });
  const [candidate, setCandidate] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [openAppraisal, setOpenAppraisal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [userList, setUserList] = useState([]);

  // ── Submit: Add Salary ───────────────────────────────────────────────────
  const onSubmit = async (data) => {
    try {
      const payload = {
        monthlyCTC: Number(data.monthlyCTC),
        effectiveFrom: dayjs(data.effectiveFrom).startOf("month").toDate(),
        isAttendanceRequired: data.isAttendanceRequired === "true",
        userId: data.userId,
      };
      const res = await postReq("/user/create_salary", payload);
      if (res.status === 200) {
        message.success("Salary Added");
        setOpen(false);
        reset();
        fetchData();
      } else {
        message.error(res.message || "Error adding salary");
      }
    } catch (error) {
      message.error(error.message || "Error adding salary");
    }
  };

  // ── Submit: Appraisal ────────────────────────────────────────────────────
  const onSubmitAppraisal = async (data) => {
    try {
      const payload = {
        userId: selected.user._id,
        newMonthlyCTC: Number(data.newMonthlyCTC),
        effectiveFrom: dayjs(data.effectiveFrom).startOf("month").toDate(),
        salaryId: selected._id,
      };
      const res = await postReq("/user/appraisal_salary", payload);
      if (res.status === 200) {
        message.success("Salary Increased Successfully");
        setOpenAppraisal(false);
        resetAppraisal();
        fetchData();
      } else {
        message.error(res.message || "Error processing appraisal");
      }
    } catch (error) {
      message.error(error.message || "Error processing appraisal");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await postReq("/user/list_salary", {
        page: paginationModel.page,
        limit: paginationModel.pageSize,
        search: paginationModel.search,
      });
      if (res.status === 200) {
        setCandidate(res.data.data);
        setTotal(res.data.total);
      } else {
        message.error(res.message || "Error fetching salary list");
        setCandidate([]);
        setTotal(0);
      }
    } catch (error) {
      message.error(error.message || "Error fetching salary list");
      setCandidate([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      const res = await getReq("/user/dropdown_user", { isActive: true });
      if (res.status === 200) {
        setUserList(res.data);
      } else {
        message.error(res.message || "Error fetching users");
      }
    } catch (error) {
      message.error(error.message || "Error fetching users");
    }
  };

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, search, page: 1 }));
    }, 600);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchUser();
  }, []);
  useEffect(() => {
    fetchData();
  }, [paginationModel]);

  const columns = useMemo(
    () => [
      {
        title: "Name",
        key: "name",
        width: 260,
        render: (_, row) => {
          const { name, profileImage } = row.user ?? {};
          return (
            <Space size={10} align="center">
              <Avatar
                alt={name?.toUpperCase()}
                src={profileImage ? `${url}${profileImage}` : undefined}
                size={28}
              >
                {name?.[0]?.toUpperCase()}
              </Avatar>
              <div>
                <div style={{ fontWeight: 400, fontSize: 13 }}>{`${name}`}</div>
                {/* <Text type="secondary" style={{ fontSize: 11 }}>
                  {empId}
                </Text> */}
              </div>
            </Space>
          );
        },
      },
      {
        title: "Designation",
        key: "role",
        width: 180,
        render: (_, row) => <Text>{row.user?.role}</Text>,
      },
      {
        title: "Monthly CTC",
        dataIndex: "monthlyCTC",
        key: "monthlyCTC",
        width: 150,
      },
      {
        title: "Effective From",
        key: "effectiveFrom",
        width: 220,
        render: (_, row) =>
          row.isActive
            ? dayjs(row.effectiveFrom).format("DD/MM/YYYY")
            : `${dayjs(row.effectiveFrom).format("DD/MM/YYYY")} - ${dayjs(row.effectiveTo).format("DD/MM/YYYY")}`,
      },
      {
        title: "Attendance Required",
        key: "isAttendanceRequired",
        width: 180,
        render: (_, row) => (row.isAttendanceRequired ? "Yes" : "No"),
      },
      {
        title: "Appraisal",
        key: "appraisal",
        width: 100,
        render: (_, row) => (
          <Tooltip title={row.isActive ? "Appraise" : "Inactive record"}>
            <Button
              type="text"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              icon={
                <RiseOutlined
                  style={{
                    color: row.isActive ? "#52c41a" : "#ff4d4f",
                    fontSize: 18,
                  }}
                />
              }
              disabled={!row.isActive}
              onClick={() => {
                setSelected(row);
                setOpenAppraisal(true);
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const attendanceOptions = [
    { label: "Yes", value: "true" },
    { label: "No", value: "false" },
  ];

  return (
    <>
      <Card bordered={false} styles={{ body: { padding: 24 } }}>
        {/* Toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 24,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <Input
            placeholder="Search Candidate Name"
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 240 }}
          />
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={() => setOpen(true)}
          >
            Add Salary
          </Button>
        </div>

        <Table
          loading={loading}
          dataSource={candidate}
          columns={columns}
          rowKey={(row) => row._id}
          scroll={{ x: "max-content" }}
          rowClassName={(row) => (row.isActive ? "" : "low-stock-row")}
          pagination={{
            current: paginationModel.page,
            pageSize: paginationModel.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20"],
            showTotal: (tot) => `Total ${tot} records`,
            onChange: (page, pageSize) =>
              setPaginationModel((prev) => ({ ...prev, page, pageSize })),
          }}
        />
      </Card>

      {/* ── Add Salary Modal ── */}
      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
          reset();
        }}
        title="Add Salary"
        width={520}
        footer={[
          <Button
            key="cancel"
            danger
            onClick={() => {
              setOpen(false);
              reset();
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit(onSubmit)}>
            Submit
          </Button>,
        ]}
        destroyOnClose
      >
        <Row gutter={[16, 8]} style={{ marginTop: 16 }}>
          {/* Monthly CTC */}
          <Col xs={24} sm={12}>
            <Controller
              name="monthlyCTC"
              control={control}
              rules={{ required: "Monthly CTC is required" }}
              render={({ field }) => (
                <Form.Item
                  label="Monthly CTC"
                  validateStatus={errors.monthlyCTC ? "error" : ""}
                  help={errors.monthlyCTC?.message}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    {...field}
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="Enter CTC"
                  />
                </Form.Item>
              )}
            />
          </Col>

          {/* User Select */}
          <Col xs={24} sm={12}>
            <Controller
              name="userId"
              control={control}
              rules={{ required: "User is required" }}
              render={({ field }) => (
                <Form.Item
                  label="Select User"
                  validateStatus={errors.userId ? "error" : ""}
                  help={errors.userId?.message}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    {...field}
                    placeholder="Select user"
                    options={userList.map((u) => ({
                      label: `${u.name}`,
                      value: u._id,
                    }))}
                  />
                </Form.Item>
              )}
            />
          </Col>

          {/* Effective From */}
          <Col xs={24} sm={12}>
            <Controller
              name="effectiveFrom"
              control={control}
              rules={{ required: "Effective month is required" }}
              render={({ field }) => (
                <Form.Item
                  label="Effective From"
                  validateStatus={errors.effectiveFrom ? "error" : ""}
                  help={errors.effectiveFrom?.message}
                  style={{ marginBottom: 0 }}
                >
                  <MonthInput
                    {...field}
                    error={!!errors.effectiveFrom}
                    placeholder="YYYY-MM"
                  />
                </Form.Item>
              )}
            />
          </Col>

          {/* Attendance Required */}
          <Col xs={24} sm={12}>
            <Controller
              name="isAttendanceRequired"
              control={control}
              render={({ field }) => (
                <Form.Item
                  label="Attendance Required"
                  style={{ marginBottom: 0 }}
                >
                  <Select {...field} options={attendanceOptions} />
                </Form.Item>
              )}
            />
          </Col>
        </Row>
      </Modal>

      {/* ── Appraisal Modal ── */}
      <Modal
        open={openAppraisal}
        onCancel={() => {
          setOpenAppraisal(false);
          resetAppraisal();
        }}
        title={`Increase Salary — ${selected?.user?.firstName ?? ""} ${selected?.user?.lastName ?? ""}`}
        width={520}
        footer={[
          <Button
            key="cancel"
            danger
            onClick={() => {
              setOpenAppraisal(false);
              resetAppraisal();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleAppraisalSubmit(onSubmitAppraisal)}
          >
            Submit
          </Button>,
        ]}
        destroyOnClose
      >
        <Row gutter={[16, 8]} style={{ marginTop: 16 }}>
          {/* New Monthly CTC */}
          <Col xs={24} sm={12}>
            <Controller
              name="newMonthlyCTC"
              control={appraisalControl}
              rules={{ required: "New Monthly CTC is required" }}
              render={({ field }) => (
                <Form.Item
                  label="New Monthly CTC"
                  validateStatus={appraisalErrors.newMonthlyCTC ? "error" : ""}
                  help={appraisalErrors.newMonthlyCTC?.message}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    {...field}
                    style={{ width: "100%" }}
                    min={0}
                    placeholder="Enter new CTC"
                  />
                </Form.Item>
              )}
            />
          </Col>

          {/* Effective From */}
          <Col xs={24} sm={12}>
            <Controller
              name="effectiveFrom"
              control={appraisalControl}
              rules={{ required: "Effective month is required" }}
              render={({ field }) => (
                <Form.Item
                  label="Effective From"
                  validateStatus={appraisalErrors.effectiveFrom ? "error" : ""}
                  help={appraisalErrors.effectiveFrom?.message}
                  style={{ marginBottom: 0 }}
                >
                  <MonthInput
                    {...field}
                    min={dayjs().subtract(1, "month").format("YYYY-MM")}
                    error={!!appraisalErrors.effectiveFrom}
                    placeholder="YYYY-MM"
                  />
                </Form.Item>
              )}
            />
          </Col>

          {/* Attendance Required */}
          <Col xs={24} sm={12}>
            <Controller
              name="isAttendanceRequired"
              control={appraisalControl}
              render={({ field }) => (
                <Form.Item
                  label="Attendance Required"
                  style={{ marginBottom: 0 }}
                >
                  <Select {...field} options={attendanceOptions} />
                </Form.Item>
              )}
            />
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default Salary;
